import type { ApplicationPublic } from '@accounts/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../i18n';
import { ApiError, testApplicationConnection } from '../../lib/api';
import { Modal, ModalForm, ModalParagraph, ModalProse } from '../../ui/Modal';
import { Callout, Chip } from '../../ui/Primitives';
import {
  checkboxRowStyle,
  checkboxStyle,
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  monoStyle,
  textFieldStyle,
} from '../../ui/styles';

/**
 * Dialogs shared by more than one admin surface. Grants can be created and revoked from the
 * Users screen, the Applications screen and the Grants screen, so their dialogs live once.
 */

/**
 * THE ONE SANCTIONED SURFACE FOR A GENERATED PASSWORD.
 *
 * It arrives in the direct response to the call that created it and exists nowhere else —
 * not in the database in readable form, not in a log, not in a later response. So this
 * dialog holds it in component state only, never writes it anywhere, and says plainly that
 * closing the window ends it.
 */
export function TemporaryPasswordModal({
  title,
  lead,
  password,
  name,
  onClose,
}: {
  title: string;
  lead: string;
  password: string;
  name: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function copy() {
    void (async () => {
      try {
        await navigator.clipboard.writeText(password);
        setCopied(true);
      } catch {
        // Clipboard refused (no permission, insecure context): the value is on screen and
        // selectable, so the operator is never stuck — nothing else to do here.
      }
    })();
  }

  return (
    <Modal
      title={title}
      lead={lead}
      onClose={onClose}
      footer={
        <button type="button" className="prim" style={modalButtonStyle} onClick={onClose}>
          {STR.modals.temporaryPassword.done}
        </button>
      }
    >
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 13 }}>
        <Callout tone="attention" style={{ padding: '16px 17px', borderRadius: 11 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
            }}
          >
            {STR.modals.temporaryPassword.banner}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 11,
              flexWrap: 'wrap',
            }}
          >
            <code
              style={{
                ...monoStyle,
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '.06em',
                padding: '9px 14px',
                borderRadius: 9,
                background: 'var(--surface)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text)',
              }}
            >
              {password}
            </code>
            <button
              type="button"
              className="ghost"
              style={{ ...modalButtonStyle, fontSize: 12.5, padding: '9px 14px' }}
              onClick={copy}
            >
              {copied ? STR.modals.temporaryPassword.copied : STR.modals.temporaryPassword.copy}
            </button>
          </div>
          <div style={{ fontSize: 12.5, marginTop: 12, lineHeight: 1.55, fontWeight: 600 }}>
            {STR.modals.temporaryPassword.finality}
          </div>
        </Callout>
        <div
          className="tx-muted"
          style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, flexWrap: 'wrap' }}
        >
          <Chip tone="attention">{STR.modals.temporaryPassword.mustChangeChip}</Chip>
          <span>{STR.modals.temporaryPassword.mustChangeSuffix(name)}</span>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The test-connection form. `POST .../test-connection` answers 200 for EVERY outcome,
 * including a wrong or revoked secret — the outcome is DATA, never an error — so a genuine
 * `mutation.isError` here means the REQUEST itself failed (network, or 400 on an empty
 * secret), never a bad credential. `message` is rendered exactly as the server wrote it: a
 * full sentence for a non-technical administrator, never a wire code.
 *
 * Self-contained on purpose: it owns its own field and its own result, so the two places
 * that render it — inline in the Applications detail pane, and pre-filled inside the
 * just-issued-secret dialog below — never share or leak state into one another.
 */
export function TestConnectionForm({
  applicationId,
  initialSecret = '',
}: {
  applicationId: string;
  initialSecret?: string;
}) {
  const [secret, setSecret] = useState(initialSecret);

  const mutation = useMutation({
    mutationFn: (value: string) => testApplicationConnection(applicationId, { secret: value }),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          className="field"
          type="text"
          value={secret}
          onChange={(event) => {
            setSecret(event.target.value);
            mutation.reset();
          }}
          placeholder={STR.applications.testConnectionPlaceholder}
          aria-label={STR.applications.testConnection}
          style={{ ...textFieldStyle, ...monoStyle, flex: 1, minWidth: 180 }}
        />
        <button
          type="button"
          className="ghost"
          disabled={secret.trim() === '' || mutation.isPending}
          onClick={() => {
            mutation.mutate(secret.trim());
          }}
          style={{ ...modalButtonStyle, fontSize: 12.5, padding: '9px 14px', flex: 'none' }}
        >
          {mutation.isPending ? STR.common.loading : STR.applications.testConnection}
        </button>
      </div>
      {mutation.isError ? (
        <Callout tone="danger" role="alert">
          {mutation.error instanceof ApiError ? mutation.error.message : STR.errors.generic}
        </Callout>
      ) : null}
      {mutation.isSuccess ? (
        // RENDERED VERBATIM. `message` is the server's own full sentence for a non-technical
        // administrator — never a wire code, never re-worded here.
        <Callout tone={mutation.data.ok ? 'positive' : 'danger'} role="status">
          {mutation.data.message}
        </Callout>
      ) : null}
      <span className="tx-subtle" style={hintStyle}>
        {STR.applications.testConnectionHint}
      </span>
    </div>
  );
}

/**
 * THE ONE SANCTIONED SURFACE FOR AN APPLICATION SECRET — the same shown-once treatment as
 * `TemporaryPasswordModal` above: attention-toned callout, large mono value, copy button
 * with its copied-state swap, finality line, same modal furniture. It never writes the
 * secret anywhere but component state; when this dialog closes, it is gone.
 *
 * It adds ONE thing that dialog does not have: an explicit acknowledgement gate. There is no
 * "reset it" recovery for a lost secret the way there is for a temporary password — losing
 * this one means issuing a whole new credential and reconfiguring the application — so Done
 * stays disabled, and `dismissable={false}` on `Modal` disables Escape and the backdrop click
 * too, until the operator has ticked that they copied it. No dismissal path bypasses the gate.
 */
export function IssuedCredentialModal({
  title,
  lead,
  application,
  secret,
  onClose,
}: {
  title: string;
  lead: string;
  application: ApplicationPublic;
  secret: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  function copy() {
    void (async () => {
      try {
        await navigator.clipboard.writeText(secret);
        setCopied(true);
      } catch {
        // Clipboard refused (no permission, insecure context): the value is on screen and
        // selectable, so the operator is never stuck — nothing else to do here.
      }
    })();
  }

  return (
    <Modal
      title={title}
      lead={lead}
      dismissable={acknowledged}
      onClose={onClose}
      footer={
        <button
          type="button"
          className="prim"
          style={modalButtonStyle}
          disabled={!acknowledged}
          onClick={onClose}
        >
          {STR.modals.issueCredential.done}
        </button>
      }
    >
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 13 }}>
        <Callout tone="attention" style={{ padding: '16px 17px', borderRadius: 11 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
            }}
          >
            {STR.modals.issueCredential.banner}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 11,
              flexWrap: 'wrap',
            }}
          >
            <code
              style={{
                ...monoStyle,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: '.01em',
                padding: '9px 14px',
                borderRadius: 9,
                background: 'var(--surface)',
                border: '1px solid var(--border-strong)',
                color: 'var(--text)',
                wordBreak: 'break-all',
                flex: 1,
                minWidth: 200,
              }}
            >
              {secret}
            </code>
            <button
              type="button"
              className="ghost"
              style={{ ...modalButtonStyle, fontSize: 12.5, padding: '9px 14px', flex: 'none' }}
              onClick={copy}
            >
              {copied ? STR.modals.issueCredential.copied : STR.modals.issueCredential.copy}
            </button>
          </div>
          <div style={{ fontSize: 12.5, marginTop: 12, lineHeight: 1.55, fontWeight: 600 }}>
            {STR.modals.issueCredential.finality}
          </div>
        </Callout>

        {/* D12 — issued does not mean admitted. Plain, not buried under the secret. */}
        {application.active ? null : (
          <Callout tone="danger" role="alert">
            {STR.modals.issueCredential.inactiveWarning}
          </Callout>
        )}

        <label style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => {
              setAcknowledged(event.target.checked);
            }}
            style={checkboxStyle}
          />
          <span style={{ fontSize: 13, fontWeight: 650 }}>
            {STR.modals.issueCredential.acknowledgeLabel}
          </span>
        </label>

        <div>
          <div className="tx-subtle" style={{ ...fieldLabelStyle, marginBottom: 8 }}>
            {STR.applications.testConnection}
          </div>
          {/* Pre-filled with the secret still on screen, so the operator can prove it works
              before this window closes and the secret is gone for good. */}
          <TestConnectionForm applicationId={application.id} initialSecret={secret} />
        </div>
      </div>
    </Modal>
  );
}

export function GrantModal({
  context,
  label,
  options,
  error,
  busy,
  onCancel,
  onSubmit,
}: {
  context: string;
  label: string;
  options: { id: string; label: string }[];
  error: string | null;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (id: string) => void;
}) {
  const [picked, setPicked] = useState(options[0]?.id ?? '');

  return (
    <Modal
      title={STR.modals.grant.title}
      lead={context}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onCancel}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={busy}
            onClick={() => {
              onSubmit(picked);
            }}
          >
            {STR.modals.grant.submit}
          </button>
        </>
      }
    >
      <ModalForm>
        {error === null ? null : (
          <Callout tone="danger" role="alert" style={{ fontWeight: 600 }}>
            {error}
          </Callout>
        )}
        <label style={{ display: 'block' }}>
          <span className="tx-muted" style={fieldLabelStyle}>
            {label}
          </span>
          <select
            className="field"
            value={picked}
            onChange={(event) => {
              setPicked(event.target.value);
            }}
            style={{ ...textFieldStyle, padding: '9px 30px 9px 11px' }}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <Callout tone="neutral">{STR.modals.grant.note}</Callout>
      </ModalForm>
    </Modal>
  );
}

/**
 * A SERVER refusal, rendered. Every one of these fires on the server's actual 409 — never on
 * a client-side guess about who the last active administrator is, because the client's
 * snapshot of that is always one write behind the truth.
 */
export function RefusalModal({
  title,
  strong,
  body,
  after,
  onClose,
}: {
  title: string;
  strong: string;
  body: string;
  after: string;
  onClose: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <button type="button" className="prim" style={modalButtonStyle} onClick={onClose}>
          {STR.common.understood}
        </button>
      }
    >
      <ModalProse>
        <Callout
          tone="danger"
          style={{ padding: '14px 15px', borderRadius: 11, fontSize: 13, lineHeight: 1.6 }}
        >
          <strong style={{ fontWeight: 700 }}>{strong}</strong> {body}
        </Callout>
        <p className="tx-muted" style={{ margin: '14px 0 0', fontSize: 13, lineHeight: 1.6 }}>
          {after}
        </p>
      </ModalProse>
    </Modal>
  );
}

export function RevokeModal({
  what,
  who,
  busy,
  onCancel,
  onSubmit,
}: {
  what: string;
  who: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <Modal
      title={STR.modals.revoke.title(what, who)}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="ghost" style={modalButtonStyle} onClick={onCancel}>
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="dbtn"
            style={modalButtonStyle}
            disabled={busy}
            onClick={onSubmit}
          >
            {STR.modals.revoke.submit}
          </button>
        </>
      }
    >
      <ModalProse>
        <ModalParagraph>{STR.modals.revoke.body}</ModalParagraph>
      </ModalProse>
    </Modal>
  );
}
